import {
    useRef,
    type ComponentProps,
    type ReactElement,
    type ReactNode,
} from "react";
import { Link, useMatch, useResolvedPath, type To } from "react-router-dom";
import { usePersistedLocationState } from "@hooks/usePersistedLocationState";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import {
    SideNavRowCopyTarget,
    type SideNavRowCopyProps,
} from "./SideNavRowCopyTarget";
import { SideNavRowLabel } from "./SideNavRowLabel";
import { useSideNavMode } from "./SideNavProvider";
import type { SideNavTarget } from "./SideNav.types";

/**
 * Whether `to` matches the location, by the rule `NavLink` uses: `end`
 * defaults to false, except that the root path matches only itself.
 */
const RouteMatch = ({
    to,
    end,
    children,
}: {
    to: To;
    end?: boolean;
    children: (isActive: boolean) => ReactElement;
}) => {
    const { pathname } = useResolvedPath(to);
    const match = useMatch({ path: pathname, end: end ?? pathname === "/" });
    return children(match !== null);
};

/**
 * Hands a row its active state: the `isActive` override when given, else the
 * route match for `to`, else inactive. Rows without `to` need no router.
 */
export const SideNavRowActive = ({
    target,
    isActive,
    children,
}: {
    target: SideNavTarget;
    isActive: boolean | undefined;
    children: (isActive: boolean) => ReactElement;
}) => {
    if (isActive !== undefined || target.to === undefined) {
        return children(isActive ?? false);
    }
    return (
        <RouteMatch to={target.to} end={target.end}>
            {children}
        </RouteMatch>
    );
};

/** An in-app link that keeps the scope's location state, like `Menu.LinkItem`. */
const RouterLink = (props: Omit<ComponentProps<typeof Link>, "state">) => {
    const state = usePersistedLocationState();
    return <Link state={state} {...props} />;
};

/**
 * A row's clickable element: a router link, an external link in a new tab, or
 * a button. Its stretched hit area covers the row. Open, the label truncates
 * and shows in full in a tooltip only while cut off; mini, the tooltip always
 * shows the label and `shortcut`.
 */
export const SideNavRowTarget = ({
    target,
    isActive,
    label,
    labelCopyId,
    labelCopyLabel,
    shortcut,
    leading,
    className,
    labelClassName,
}: {
    target: SideNavTarget;
    isActive: boolean;
    label: string;
    labelCopyId?: string | number;
    labelCopyLabel?: string;
    shortcut?: string;
    leading?: ReactNode;
    className: string;
    labelClassName: string;
}) => {
    const mode = useSideNavMode();
    const labelRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: labelRef,
        isTruncated: isOverflowingX,
    });
    const ariaCurrent = isActive || undefined;

    const renderTarget = (copyProps: SideNavRowCopyProps) => {
        const content = (
            <>
                {leading}
                <SideNavRowLabel
                    label={label}
                    labelClassName={labelClassName}
                    labelRef={labelRef}
                />
            </>
        );

        if (target.to !== undefined) {
            return (
                <RouterLink
                    to={target.to}
                    onClick={target.onClick}
                    className={className}
                    aria-current={isActive ? "page" : undefined}
                    {...copyProps}
                >
                    {content}
                </RouterLink>
            );
        }
        if (target.href !== undefined) {
            return (
                <a
                    href={target.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={target.onClick}
                    className={className}
                    aria-current={ariaCurrent}
                    {...copyProps}
                >
                    {content}
                </a>
            );
        }
        return (
            <button
                type="button"
                onClick={target.onClick}
                className={className}
                aria-current={ariaCurrent}
                {...copyProps}
            >
                {content}
            </button>
        );
    };

    return (
        <SideNavRowCopyTarget
            labelCopyId={labelCopyId}
            labelCopyLabel={labelCopyLabel}
        >
            {(copyProps) => {
                const rowTarget = renderTarget(copyProps);

                if (mode === "open") {
                    return (
                        <Tooltip
                            content={label}
                            side="right"
                            {...truncationTooltipProps}
                        >
                            {rowTarget}
                        </Tooltip>
                    );
                }
                return shortcut === undefined ? (
                    <Tooltip content={label} side="right">
                        {rowTarget}
                    </Tooltip>
                ) : (
                    <Tooltip content={label} shortcut={shortcut} side="right">
                        {rowTarget}
                    </Tooltip>
                );
            }}
        </SideNavRowCopyTarget>
    );
};
